import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { Brain, Home, Library, Plus, Search } from "lucide-react";
import { haptic } from "../lib/haptics";
import { QuickCaptureSheet } from "./QuickCaptureSheet";

const LEFT_TABS = [
  { to: "/", label: "Home", icon: Home },
  { to: "/library", label: "Library", icon: Library },
];

const RIGHT_TABS = [
  { to: "/search", label: "Search", icon: Search },
  { to: "/thinking", label: "Think", icon: Brain },
];

/**
 * True while the on-screen keyboard is likely covering the bottom edge.
 *
 * Belt-and-braces on purpose: iOS (especially installed PWAs) sometimes
 * reports a stale visualViewport height after the keyboard closes or the app
 * resumes from the background, which used to leave the tab bar stuck
 * translated off the bottom of the screen. The keyboard can only be "open"
 * while an editable element actually has focus, so we gate on that and
 * re-check on focus changes and app resume — never on viewport height alone.
 */
function useKeyboardOpen() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;

    const editableFocused = () => {
      const el = document.activeElement;
      return (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        (el instanceof HTMLElement && el.isContentEditable)
      );
    };

    const compute = () => {
      const occluded = vv ? window.innerHeight - vv.height > 140 : false;
      setOpen(occluded && editableFocused());
    };
    // focusout fires before focus settles on the next element; defer a beat.
    const computeSoon = () => setTimeout(compute, 60);

    vv?.addEventListener("resize", compute);
    window.addEventListener("focusin", compute);
    window.addEventListener("focusout", computeSoon);
    document.addEventListener("visibilitychange", computeSoon);
    return () => {
      vv?.removeEventListener("resize", compute);
      window.removeEventListener("focusin", compute);
      window.removeEventListener("focusout", computeSoon);
      document.removeEventListener("visibilitychange", computeSoon);
    };
  }, []);
  return open;
}

/**
 * Fixed bottom navigation for phones (<md). Five slots: Home, Library, a
 * raised capture FAB in the center, Search, Thinking Space. Hidden while the
 * virtual keyboard is up so it never floats above an input.
 */
export function MobileTabBar() {
  const [captureOpen, setCaptureOpen] = useState(false);
  const keyboardOpen = useKeyboardOpen();
  const location = useLocation();

  // Lets CSS (--mobile-tab-bar) reserve scroll space only while the bar exists.
  useEffect(() => {
    document.documentElement.classList.add("has-tab-bar");
    return () => document.documentElement.classList.remove("has-tab-bar");
  }, []);

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `flex flex-col items-center justify-center gap-0.5 flex-1 h-full transition-colors active:opacity-60 ${
      isActive ? "text-accent" : "text-muted-foreground/70"
    }`;

  const renderTab = (tab: (typeof LEFT_TABS)[number]) => {
    const isCurrent =
      tab.to === "/" ? location.pathname === "/" : location.pathname.startsWith(tab.to);
    return (
      <NavLink
        key={tab.to}
        to={tab.to}
        className={tabClass}
        end={tab.to === "/"}
        onClick={() => {
          haptic("light");
          // iOS convention: tapping the tab you're already on returns to top.
          if (isCurrent) {
            const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            document.getElementById("root")?.scrollTo({
              top: 0,
              behavior: reduceMotion ? "auto" : "smooth",
            });
          }
        }}
      >
        {({ isActive }) => (
          <>
            <tab.icon size={25} strokeWidth={isActive ? 2.2 : 1.8} />
            <span
              style={{
                fontSize: "0.62rem",
                letterSpacing: "0.02em",
                fontWeight: isActive ? 500 : 400,
              }}
            >
              {tab.label}
            </span>
          </>
        )}
      </NavLink>
    );
  };

  return (
    <>
      <nav
        className={`fixed inset-x-0 bottom-0 z-50 md:hidden bg-card/90 backdrop-blur-lg border-t border-border/40 transition-transform duration-200 ${
          keyboardOpen ? "translate-y-full" : "translate-y-0"
        }`}
        style={{ paddingBottom: "var(--safe-bottom)" }}
        aria-label="Primary"
      >
        <div className="flex items-stretch h-16">
          {LEFT_TABS.map(renderTab)}

          {/* Center capture FAB — the most prominent control in the shell */}
          <div className="flex-1 flex items-center justify-center">
            <button
              onClick={() => {
                haptic("medium");
                setCaptureOpen(true);
              }}
              className="-mt-6 w-14 h-14 rounded-full bg-accent text-accent-foreground shadow-lg shadow-accent/30 flex items-center justify-center active:scale-90 transition-transform duration-150 ease-out"
              aria-label="Capture a thought"
            >
              <Plus size={31} strokeWidth={2.2} />
            </button>
          </div>

          {RIGHT_TABS.map(renderTab)}
        </div>
      </nav>

      <QuickCaptureSheet open={captureOpen} onClose={() => setCaptureOpen(false)} />
    </>
  );
}
